import { eventQBus } from "@otto-ec/global-resources/event-q-bus";
import { moduleLogger } from "../util";
import { ActivationClient } from "./ActivationClient";
import { ActivationStatus, type Detailview, type Tracking } from "./Detailview";
import { ActivationTrigger } from "./events/ActivationTrigger";
import type { AddToBasketEvent } from "./events/AddToBasketEvent";
import { registerSeenHandlerOnElement } from "./tracking/SeenHandler";
import type { TrackingService } from "./tracking/TrackingService";
import { attributeNameTriggerReload } from "./detailview_component";
import type { HTMLOcSnackbarV1Element } from "@otto-ec/otto-components/snackbar";

const log = moduleLogger.scope("detailview.service");

/*                                                                                                    */
/*                                                                              */
const MAX_CLEANUP_PERIOD_IN_MILLIS = 20 * 24 * 60 * 60 * 1000;

/*                                                                                              */
const ACTIVATION_TIMEOUT_LOADER = 400;

const wait = (timeout: number): Promise<void> => {
	return new Promise((resolve) => setTimeout(resolve, timeout));
};

export class DetailviewService {
	constructor(private readonly trackingService: TrackingService) {}

	private readonly activationClient = new ActivationClient();

	public initialize(variationId: string, customComponentElement: HTMLElement, eventMergeId: string | null) {
		const detailviewHtmlElement = customComponentElement.firstElementChild;
		if (!(detailviewHtmlElement && detailviewHtmlElement instanceof HTMLElement)) {
			return;
		}

		const sublineElement = detailviewHtmlElement.querySelector<HTMLElement>(".benefit-detailview_partnerbenefit-subline, .benefit-detailview__early-access__action-container");
		if (!sublineElement) {
			log.error("Subline element not found");
			return;
		}

		const detailview = buildDetailview(detailviewHtmlElement, sublineElement, variationId);
		if (!detailview) {
			return;
		}

		this.handleAlreadyActivatedBenefit(detailview);
		initExpirationHandler(detailview, customComponentElement);

		if (detailview.activationStatus === ActivationStatus.DEACTIVATED || detailview.isEarlyAccess) {
			this.handleSheetActivation(detailview, customComponentElement);
		}

		log.info("Send load tracking event and set visible handler");
		this.trackingService.sendDetailviewBenefitLoadedEvent(detailview, eventMergeId);
		registerSeenHandlerOnElement(detailviewHtmlElement, () => {
			this.trackingService.sendDetailviewBenefitVisibleEvent(detailview);
		});
		const toAppButtonElement = customComponentElement.querySelector<HTMLElement>("#benefit-detailview_app_btn");
		if (toAppButtonElement) {
			toAppButtonElement.addEventListener("click", () => {
				this.trackingService.sendDetailviewBenefitClickEvent(detailview);
			});
		}

		eventQBus.on("ft1.order-core.addToBasketResult", addToBasketHandler(this.trackingService, detailview).bind(this));

		/*                             */
		const { activate } = window.otto.ft9.benefit.components.activationSlider;

		return activate.subscribe((event: any) => {
			if (!detailviewHtmlElement.contains(event.instance)) return;
			if (event.instance.dataset.benefitId !== detailview.benefitId) return;

			const activationUrl = event.instance.dataset.activationUrl;
			if (!activationUrl) {
				log.error("Missing activationUrl");
				return;
			}

			const errorLoggingPayload = {
				benefit_id: detailview.benefitId,
				benefit_active_until: detailview.activeUntil,
			};

			const startedMs = new Date().getMilliseconds();

			this.activationClient.activateViaUrl(activationUrl, errorLoggingPayload).then(async (success) => {
				const endedMs = new Date().getMilliseconds();

				/*                                                                                                                                                */
				const LOADING_STATE_TIMEOUT_MS = Math.max(0, ACTIVATION_TIMEOUT_LOADER + 200 - (endedMs - startedMs));

				if (success) {
					eventQBus.emit("ft9.benefit.active", {
						id: detailview.benefitId,
						active: true,
						variationId: variationId,
						trigger: ActivationTrigger.ACTIVATED_BY_USER,
					});

					await wait(LOADING_STATE_TIMEOUT_MS);
					event.instance.setAttribute("is-activated", "true");
				} else {
					if (window.o_util.toggle.get("ftnine_migrate_to_oc_snackbar", false)) {
						const snackbar = detailviewHtmlElement.querySelector<HTMLOcSnackbarV1Element>("#benefit-detailview_snackbar--error");
						if (snackbar) {
							snackbar.open = true;
						}
					} else {
						eventQBus.emit("assets.snackbar.trigger", {
							text: "Das hat leider nicht geklappt. Bitte probiere es noch einmal, danke.",
						});
					}

					await wait(LOADING_STATE_TIMEOUT_MS);
					event.instance.reset();
				}
				this.trackingService.sendDetailviewBenefitSwipeEvent(detailview, success);
			});
		});
	}

	private handleAlreadyActivatedBenefit(detailview: Detailview) {
		if (detailview.activationStatus === ActivationStatus.ACTIVATED) {
			eventQBus.emit("ft9.benefit.active", {
				id: detailview.benefitId,
				active: true,
				variationId: detailview.variationId,
				trigger: ActivationTrigger.FROM_DB_STORAGE,
			});
		}
	}

	private handleSheetActivation(detailview: Detailview, detailviewHtml: HTMLElement) {
		eventQBus.on("ft9.benefit.sheet.activated", ({ benefitId }) => {
			if (!benefitId || (benefitId !== detailview.benefitId && benefitId !== detailview.frameId)) return;
			eventQBus.emit("ft9.benefit.active", {
				id: detailview.benefitId,
				active: true,
				variationId: detailview.variationId,
				trigger: ActivationTrigger.ACTIVATED_BY_USER,
			});
			setTimeout(() => {
				detailviewHtml.setAttribute(attributeNameTriggerReload, `${new Date().getTime()}`);
			}, 500); /*                                                                                                  */
		});
	}
}

function buildDetailview(detailviewBenefitElement: HTMLElement, sublineElement: HTMLElement, variationId: string): Detailview | null {
	const isAppOnly = sublineElement.dataset.isAppOnly === "true";
	const activationStatus = sublineElement.dataset.activationStatus as ActivationStatus;
	const isEarlyAccess = sublineElement.dataset.isEarlyAccess === "true";

	const benefitId = sublineElement.dataset.benefitId;
	if (!benefitId) {
		log.error("Missing benefitId");
		return null;
	}
	const frameId = sublineElement.dataset.frameId;
	const activeUntil = sublineElement.dataset.activeUntil;
	if (!activeUntil) {
		log.error("Missing activeUntil");
		return null;
	}

	const tracking = detailviewBenefitElement.dataset.tracking;

	if (!tracking) {
		log.error("Missing tracking");
		return null;
	}

	return {
		benefitId,
		frameId,
		activeUntil,
		activationStatus,
		tracking: JSON.parse(tracking) as Tracking,
		variationId,
		isAppOnly,
		isEarlyAccess,
	};
}

function initExpirationHandler(detailview: Detailview, detailviewHtml: HTMLElement) {
	const end: Date = new Date(detailview.activeUntil);
	const delayInMillis: number = Math.min(Math.max(end.getTime() - new Date().getTime() - 1000, 1), MAX_CLEANUP_PERIOD_IN_MILLIS);

	log.info(`Expiration delay: ${delayInMillis} ms`);

	window.setTimeout(() => {
		/*                                                                                */
		if (detailview.isEarlyAccess) {
			detailviewHtml.setAttribute(attributeNameTriggerReload, `${new Date().getTime()}`);
			return;
		}

		eventQBus.emit("ft9.benefit.active", {
			id: detailview.benefitId,
			active: false,
			variationId: detailview.variationId,
			trigger: ActivationTrigger.EXPIRED_BENEFIT,
		});
	}, delayInMillis);
}

function addToBasketHandler(trackingService: TrackingService, detailview: Detailview) {
	return (event: AddToBasketEvent) => {
		if (!event.variationId || !event.eventMergeId) {
			return;
		}
		if (event.variationId !== detailview.variationId) {
			/*                                                                               */
			/*                                                                                                 */
			return;
		}
		trackingService.sendDetailviewBenefitMergeEvent(detailview, event.eventMergeId);
	};
}
